import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import {
  ApolloErrorMapKeysType,
  ApolloErrorMapTypesType,
  ApolloErrorType,
  ApolloExtensionsType,
  ApolloOperationType
} from '../models/apollo-errors';
import { resolveErrorMapResponse } from '../helpers/remapApolloErrors';
import { invitationDetailsVar } from '../providers/AppProvider';

export const getApolloError = (
  error: ApolloErrorMapKeysType,
  operation: ApolloOperationType,
  extensions: ApolloExtensionsType = {}
): JSX.Element => {
  let response: JSX.Element | undefined;
  if (ApolloErrorsObject.hasOwnProperty(error)) {
    const errorObjectList = ApolloErrorsObject[error];
    response = resolveErrorMapResponse(errorObjectList, operation, extensions);
  }
  return <Fragment>{response}</Fragment>;
};

const TYPE_ALL_SOCIAL_PROFILES: ApolloErrorMapTypesType[] = [
  'TWITTER_SOCIAL_PROFILE',
  'PINTEREST_SOCIAL_PROFILE',
  'LINKED_IN_SOCIAL_PROFILE',
  'SOCIAL_PROFILE',
  'FACEBOOK_SOCIAL_PROFILE',
  'INSTAGRAM_SOCIAL_PROFILE',
  'GOOGLE_SOCIAL_PROFILE'
];

export const GENERIC_ERROR_MESSAGE = 'Sorry, something went wrong.';

export const ApolloErrorsObject: ApolloErrorType = {
  GENERIC_ERROR_MESSAGE: [
    {
      response: <>{GENERIC_ERROR_MESSAGE}</>
    }
  ],
  ALREADY_EXISTS: [
    {
      operation: 'createUserInvitation',
      extensions: {
        type: 'USER_ORGANIZATION_ASSIGNMENT'
      },
      response: <>An invitation for this email address already exists</>
    },
    {
      response: <>The resource you are trying to create already exists.</>
    }
  ],
  BULK_OPERATION_FAILED: [],
  CATEGORY_NOT_ARCHIVED: [
    {
      response: <>This category cannot be deleted as it is not archived</>
    }
  ],
  EXTERNAL_RESOURCE_ERROR: [
    {
      operation: 'createAutoImport',
      response: <>URL doesn&apos;t contain a valid RSS feed</>
    },
    {
      operation: 'searchRssPosts',
      response: <>Error getting results for this search</>
    }
  ],
  EXTERNAL_RESOURCE_INVALID: [
    {
      operation: 'createAutoImport',
      response: <>URL doesn&apos;t contain a valid RSS feed</>
    },
    {
      operation: 'searchRssPosts',
      response: <>URL doesn&apos;t contain a valid RSS feed</>
    }
  ],
  EXTERNAL_RESOURCE_NOT_FOUND: [
    {
      operation: 'createAutoImport',
      response: <>URL doesn&apos;t contain a valid RSS feed</>
    },
    {
      operation: 'searchRssPosts',
      response: <>Error getting results for this search</>
    }
  ],
  FACEBOOK_LIKED_PAGES_NOT_SUPPORTED: [],
  FILE_TYPE_INVALID: [
    {
      response: <>File type invalid</>
    }
  ],
  FILE_SIZE_TOO_LARGE: [
    {
      response: <>File size too large</>
    }
  ],
  FILE_PARSING_ERROR: [
    {
      response: <>File parsing error.</>
    }
  ],
  HTTP_RATE_LIMITED: [{ response: <>Rate limit exceeded. Please try again later.</> }],
  INVALID_SOCIAL_NETWORK: [],
  LOGIN_INVALID_SOCIAL_NETWORK: [{ response: <>Social Login not supported for this social network.</> }],
  MISSING_ENTITY_ID: [],
  NOT_ENOUGH_WORKSPACES: [
    {
      response: <>The operation would leave the user in an organization without being assigned to any workspaces.</>
    }
  ],
  NOT_FOUND: [
    {
      operation: 'reconnectSocialProfile',
      response: <>Social Profile could not been re-connected since it does not exist.</>
    },
    {
      operation: 'reconnectSocialProfile',
      extensions: {
        type: TYPE_ALL_SOCIAL_PROFILES
      },
      response: <>Make sure you&apos;re logged into the same Profile that you&apos;re trying to reconnect.</>
    },
    {
      operation: 'removeSocialProfile',
      response: <>Social Profile could not been removed since it does not exist.</>
    },
    {
      operation: 'resetPassword',
      extensions: {
        type: 'RESET_PASSWORD'
      },
      response: (
        <>
          The password reset URL you provided is not valid. Perhaps you have incorrectly copied and pasted the link from
          your email, or the link has already been used. Please try to{' '}
          <Link to={'/forgot-password'}>request your password again</Link>, or{' '}
          <a href={'/contact'} target={'_blank'} rel="noreferrer">
            contact us
          </a>{' '}
          if you are still having problems.
        </>
      )
    },
    {
      extensions: {
        type: 'USER'
      },
      response: <>User does not exist.</>
    },
    {
      operation: 'joinOrganizationFromInvitation',
      extensions: {
        type: 'USER_INVITATION'
      },
      response: (
        <>
          The invitation token is not valid. Perhaps you have incorrectly copied and pasted the link from your email, or
          the link has already been used.
        </>
      )
    },
    {
      operation: 'getInvitationDetails',
      extensions: {
        type: 'USER_INVITATION'
      },
      response: (
        <>
          The invitation token is not valid. Perhaps you have incorrectly copied and pasted the link from your email, or
          the link has already been used.
        </>
      )
    },
    {
      operation: 'updateUserInvitation',
      extensions: {
        type: 'USER_INVITATION'
      },
      response: <>The invitation token is not valid.</>
    },
    {
      operation: 'externalSecondaryProfiles',
      extensions: {
        type: 'LINKED_IN_SOCIAL_PROFILE'
      },
      response: <>You have no linkedin companies linked to this profile.</>
    }
  ],
  OPML_FILE_EMPTY: [
    {
      response: <>OPML file is empty.</>
    }
  ],
  OPML_FILE_STRUCTURE_INVALID: [
    {
      response: <>OPML file structure is invalid.</>
    }
  ],
  PAGINATION_CURSOR_INVALID: [],
  PLAN_LIMIT_EXCEEDED: [
    {
      response: ext => <pre style={{ fontFamily: 'inherit', textWrap: 'wrap' }}>{ext.message}</pre>
    }
  ],
  POST_ALREADY_PUBLISHED: [
    {
      response: <>The post(s) you are trying to update have already been published.</>
    }
  ],
  POST_NOT_TRASHED: [
    {
      response: <>The post(s) you are trying to delete must be trashed before they can be deleted.</>
    }
  ],
  POST_PUBLISHING_NOW: [
    {
      response: <>The post(s) you are trying to update are currently being published.</>
    }
  ],
  RESET_ALREADY_AUTHENTICATED: [
    {
      response: <>You must be logged out to reset your password.</>
    }
  ],
  RESET_NO_USER_ASSOCIATED: [
    {
      operation: 'resetPassword',
      response: (
        <>
          The password reset URL you provided is not valid. Perhaps you have incorrectly copied and pasted the link from
          your email, or the link has already been used. Please try to{' '}
          <Link to={'/forgot-password'}>request your password again</Link>, or{' '}
          <a href={'/contact'} target={'_blank'} rel="noreferrer">
            contact us
          </a>{' '}
          if you are still having problems.
        </>
      )
    }
  ],
  SECURITY_NOT_AUTHENTICATED: [
    {
      response: <>Not authenticated, please log in.</>
    }
  ],
  SECURITY_NO_REFRESH_TOKEN: [
    {
      response: <>Could not detect refresh token, please log in.</>
    }
  ],
  SECURITY_PASSWORD_INVALID: [
    {
      operation: 'updateUser',
      response: <>Current password is incorrect.</>
    },
    {
      response: <>Password is incorrect.</>
    }
  ],
  SECURITY_REFRESH_TOKEN_INVALID: [
    {
      response: <>Invalid refresh token, please log in.</>
    }
  ],
  SECURITY_USER_NOT_FOUND: [
    {
      response: (
        <>
          The email & password combination you provided is incorrect.{' '}
          <a href={'/contact'} target={'_blank'} rel="noreferrer">
            Contact us
          </a>{' '}
          if you need help logging in.
        </>
      )
    }
  ],
  SIGNUP_ALREADY_AUTHENTICATED: [
    {
      response: <>You are already authenticated.</>
    }
  ],
  SIGNUP_EMAIL_ALREADY_REGISTERED: [
    {
      response: (
        <>
          There is already a SmarterQueue account registered with this email address. Would you like to{' '}
          <Link
            reloadDocument
            to={'/login'}
            onClick={() => {
              /* TODO remove reloadDocument and this session storage once React app is the only app - this is for the legacy login portal. */
              if (invitationDetailsVar()) {
                sessionStorage.setItem('invitation', JSON.stringify(invitationDetailsVar()));
              }
            }}>
            log in
          </Link>{' '}
          instead?
        </>
      )
    }
  ],
  SIGNUP_PASSWORD_MISSING: [
    {
      response: <>Signup credentials are missing, please try again.</>
    }
  ],
  SIGNUP_SOCIAL_NETWORK_ALREADY_ASSIGNED: [
    {
      response: (
        <>
          There is already a SmarterQueue account registered with this social account. Would you like to{' '}
          <Link
            reloadDocument
            to={'/login'}
            onClick={() => {
              /* TODO remove reloadDocument and this session storage once React app is the only app - this is for the legacy login portal. */
              if (invitationDetailsVar()) {
                sessionStorage.setItem('invitation', JSON.stringify(invitationDetailsVar()));
              }
            }}>
            log in
          </Link>{' '}
          instead?
        </>
      )
    }
  ],
  SOCIAL_ACCESS_TOKEN_INVALID: [
    {
      response: <>Could not access social network using stored access token.</>
    }
  ],
  SOCIAL_AUTH_TOKEN_OWNER_MISMATCH: [
    {
      operation: 'externalSecondaryProfiles',
      extensions: {
        type: 'FACEBOOK_SOCIAL_PROFILE'
      },
      response: <>Make sure you are logged into Facebook with the correct profile and try again.</>
    },
    {
      operation: 'externalSecondaryProfiles',
      extensions: {
        type: 'LINKED_IN_SOCIAL_PROFILE'
      },
      response: <>Make sure you are logged into LinkedIn with your own LinkedIn Profile and try again</>
    },
    {
      operation: 'reconnectSocialProfile',
      extensions: {
        type: ['FACEBOOK_SOCIAL_PROFILE', 'INSTAGRAM_SOCIAL_PROFILE']
      },
      response: <>Make sure you are logged into Facebook with your own Facebook Profile and try again</>
    },
    {
      operation: 'reconnectSocialProfile',
      extensions: {
        type: 'LINKED_IN_SOCIAL_PROFILE'
      },
      response: <>Make sure you are logged into LinkedIn with your own LinkedIn Profile and try again</>
    },
    {
      response: <>Make sure you are logged into the correct profile and try again.</>
    }
  ],
  SOCIAL_AUTH_WRONG_NETWORK: [
    {
      response: <>Social auth client token supplied is for the wrong social network.</>
    }
  ],
  SOCIAL_NETWORK_NOT_AUTHORIZED: [
    {
      response: <>You have not granted authorization to connect your Profiles.</>
    }
  ],
  SOCIAL_NETWORK_NOT_CONNECTED: [
    {
      operation: 'connectMainSocialProfile',
      response: <>This Profile could not be connected to a SmarterQueue account.</>
    },
    {
      operation: 'reconnectSocialProfile',
      response: <>This Profile is not linked to a SmarterQueue account.</>
    },
    {
      operation: 'loginWithSocial',
      response: (
        <>
          <p>
            {/*That Profile is not linked to a SmarterQueue account. You can <Link to={'/signup'}>Sign up</Link> instead.{' '}*/}
            That Profile is not linked to a SmarterQueue account.{' '}
          </p>
          <p>
            <a href={'/contact'} target={'_blank'} rel="noreferrer">
              Contact us
            </a>{' '}
            if you need help.
          </p>
        </>
      )
    },
    {
      operation: 'refreshPinterestBoards',
      response: (
        <>
          <p>
            We could not refresh your Pinterest Boards. Social profile is not connected to any SmarterQueue account.
          </p>
          <p>
            <Link to={'/settings/profiles'}>Reconnect your Pintertest profile</Link>.
          </p>
        </>
      )
    },
    {
      response: <>Social profile is not connected to any SmarterQueue account.</>
    }
  ],
  SOCIAL_PROFILE_CONNECTED_DIFFERENT_ORGANIZATION: [
    {
      operation: 'connectBitly',
      extensions: { type: ['BITLY'] },
      response: <>This Bitly account is already connected to another Organization in SmarterQueue.</>
    },
    {
      response: <>This Profile is already connected to another Organization in SmarterQueue.</>
    }
  ],
  SOCIAL_PROFILE_CONNECTED_DIFFERENT_WORKSPACE: [
    {
      response: <>This Profile is already connected to another Workspace.</>
    }
  ],
  THIRD_PARTY_INSUFFICIENT_PERMISSIONS: [
    {
      operation: 'reconnectSocialProfile',
      extensions: {
        type: ['FACEBOOK_SOCIAL_PROFILE', 'INSTAGRAM_SOCIAL_PROFILE']
      },
      response: (
        <>
          Make sure you grant all permissions for the page.
          <a rel={'nofollow'} target={'_blank'}>
            Get help
          </a>
        </>
      )
    },
    {
      response: <>Make sure you grant all permissions.</>
    }
  ],
  THIRD_PARTY_RATE_LIMITED: [
    {
      response: <>Something went wrong with an external API request. Please try again later.</>
    }
  ],
  THIRD_PARTY_TOKEN_INVALID: [
    {
      response: <>Something went wrong with an external API request. Please try again later.</>
    }
  ],
  SOCIAL_PROFILE_INSTAGRAM_API_MISMATCH: [],
  SOCIAL_PROFILE_RECONNECT_MISMATCH: [
    {
      operation: 'reconnectSocialProfile',
      extensions: {
        type: 'INSTAGRAM_SOCIAL_PROFILE'
      },
      response: (
        <>
          Make sure you are currently logged into Instagram as the Instagram Profile you are trying to reconnect and try
          again.
          <br />
          <a rel={'nofollow'} target={'_blank'}>
            Get help
          </a>
        </>
      )
    },
    {
      response: (
        <>
          Requested operation could not be performed, since you are logged in as different user to that social network.
        </>
      )
    }
  ],
  THIRD_PARTY_API_ERROR: [
    {
      response: <>Something went wrong with an external API request. Please try again later.</>
    }
  ],
  THIRD_PARTY_UNAUTHORIZED: [
    {
      operation: 'reconnectSocialProfile',
      extensions: {
        type: ['FACEBOOK_SOCIAL_PROFILE', 'INSTAGRAM_SOCIAL_PROFILE']
      },
      response: (
        <>
          Make sure you are currently logged into Facebook as an admin of that page or group and try again.
          <a rel={'nofollow'} target={'_blank'}>
            Get help
          </a>
        </>
      )
    },
    {
      operation: 'reconnectSocialProfile',
      extensions: {
        type: 'LINKED_IN_SOCIAL_PROFILE'
      },
      response: (
        <>
          Make sure you are currently logged into LinkedIn as an admin of that company and try again.
          <a rel={'nofollow'} target={'_blank'}>
            Get help
          </a>
        </>
      )
    },
    {
      response: <>You are not authorized to perform this action.</>
    }
  ],
  UNAUTHORIZED: [
    {
      operation: 'createWorkspace',
      extensions: {
        type: 'WORKSPACE'
      },
      response: <>You don&apos;t have sufficient permission to create this Workspace.</>
    },
    {
      operation: 'removeSocialProfile',
      response: <>You don&apos;t have sufficient permission to delete this Social Profile.</>
    },
    {
      response: <>You don&apos;t have sufficient permission to perform this action.</>
    }
  ],
  QUEUE_STILL_CALCULATING: [],
  VALIDATION: [
    {
      response: e => <p style={{ whiteSpace: 'pre-line' }}>{e.message}</p>
    }
  ],
  SECURITY_IMPERSONATION_FAILED: [
    {
      response: <>You don&apos;t have sufficient permission to perform this action.</>
    }
  ],
  VIDEO_FILE_HAS_NO_FRAMES: [
    {
      response: <>No frames detected in video</>
    }
  ],
  AI_CAPTION_ERROR: [
    {
      response: <>There was an error generating your caption. Please try again</>
    }
  ],
  AI_CAPTION_MODERATED: [
    {
      response: <>The prompt contains inappropriate content and has been moderated</>
    }
  ],
  AI_CAPTION_TIME_LIMIT_EXCEEDED: [
    {
      response: <>The API took too long to complete your caption. Please try again.</>
    }
  ],
  SECURITY_USER_NOT_IN_ALPHA: [],
  INVITED_USER_CANT_BE_MIGRATED_MEMBER: [
    {
      response: (
        <>
          Due to technical reasons, you are not currently able to join multiple organizations with this user account.
          <br />
          <a href={'/contact'} target={'_blank'} rel="noreferrer">
            Contact us
          </a>{' '}
          for more information.
        </>
      )
    }
  ]
};
